import React, { FC, Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Alert, Button, ButtonBase } from '@mui/material';
import { useStore } from '@/components/store/Store';
import { useBlockClipboardContext } from '@/editor/lib/BlockClipboardContext';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import AddBlockButton from './AddBlockButton';
import Section from '../../components/Section/Section';
import Styles from '../../assets/js/Styles';
import { Page } from '../../declarations/models/Page';
import { Draggable } from '../../declarations/Draggable';
import BlockSettings from './BlockSettings';
import { BaseBlock } from '../../declarations/models/blocks/BaseBlock';
import { deepCopy } from '../../utils/object';
import { useControlledFieldArray } from '../../hooks/useControlledFieldArray';
import { useEditorConfiguration } from '../lib/configuration/EditorConfigurationContext';
import EditorBlock from './EditorBlock/EditorBlock';
import { useCurrentSection } from './CurrentSectionProvider';
import CommonSectionContent from './CommonSectionContent';
import { BlocksPath, CurrentBlockProvider } from './CurrentBlockProvider';
import SectionsController from '../../components/Section/SectionsController';
import { BlockType } from '../../declarations/models/BlockType';
import { EditorEventType, EventManagerApi } from '../lib/eventManager/EditorEventManager';
import { SectionBlocksContext } from './SectionBlocksContext';
import { moduleClipboard } from '../lib/useModuleClipboard';
import { useEditorModel } from '../lib/components/EditorDataProvider';
import { canChooseAddPosition, useAddBlockPosition } from './useAddBlockPosition';

export interface PageEditorSectionContentParams {
  useEventManager?: EventManagerApi;
  /** Moves the blocks after the given index to a new section below. Leave out where sections can't be split. */
  onSplitSection?: (afterBlockIndex: number) => void;
}

/** A "split here" line between two blocks, shown while the section is in split mode */
const SplitSectionGap: FC<{ onSplit: () => void }> = ({ onSplit }) => {
  const { t } = useTranslation('common');
  return (
    <ButtonBase
      onClick={onSplit}
      aria-label={t('splitSection')}
      sx={{
        width: '100%',
        py: 0.5,
        gap: 1,
        fontSize: '0.75rem',
        color: Styles.Colors.DARK_GREY,
        borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
        '&::before, &::after': {
          content: '""',
          flex: 1,
          borderTop: `2px dashed currentColor`,
        },
        '&:hover, &:focus-visible': { color: 'error.main' },
      }}>
      <MaterialSymbol name='content_cut' sx={{ fontSize: 18 }} />
      {t('splitSection')}
    </ButtonBase>
  );
};

export const PageEditorSectionContent: FC<PageEditorSectionContentParams> = ({ useEventManager, onSplitSection }) => {
  const { t } = useTranslation('common');
  const editorConfiguration = useEditorConfiguration();
  const { sectionPath, sectionIndex, sectionType } = useCurrentSection();
  const [settingsVisible, setSettingsVisible] = useState<boolean>(true);
  const [splitMode, setSplitMode] = useState<boolean>(false);

  const {
    fields: blocks,
    append,
    move,
    remove,
    insert,
  } = useControlledFieldArray<Page, BlocksPath>({ name: `${sectionPath}.blocks` });

  const { copy, paste, cut, currentCutBlockId } = useBlockClipboardContext();
  const page = useEditorModel<Page>();
  const { enqueueSnackbar } = useSnackbar();
  const { state } = useStore();
  // The clipboard kept across pages is tried out by superadmins first
  const canUseModuleClipboard = !!state.currentUser?.user?.is_super_admin;

  const [addPosition] = useAddBlockPosition();

  // "Top" is below the article title, which always stays first
  const getAddIndex = () => {
    if (addPosition === 'bottom' || !canChooseAddPosition(sectionType)) return blocks.length;
    const firstMovable = blocks.findIndex((b) => b.type !== BlockType.ARTICLE_TITLE);
    return firstMovable < 0 ? blocks.length : firstMovable;
  };

  const addBlockAt = (index: number, item: BaseBlock) => {
    if (index >= blocks.length) {
      append(item);
      return;
    }
    // Same as moving: settings must be hidden while the indexes shift
    setSettingsVisible(false);
    insert(index, item);
    setTimeout(() => setSettingsVisible(true), 100);
  };

  const appendAndOpen = (item: BaseBlock) => {
    const index = getAddIndex();
    addBlockAt(index, item);
    // Wrap in timeout to allow the new component to render before firing the event. It's the new component that's listening for the event.
    if (useEventManager) {
      setTimeout(() => {
        useEventManager.fireEvent(EditorEventType.BLOCK_OR_SECTION_ADDED, {
          anchorId: `${sectionPath}.blocks.${index}`,
        });
      });
    }
  };
  const handlePasteBlock = () => {
    const val = paste();
    if (val != null) {
      const index = getAddIndex();
      useEventManager?.fireEvent(EditorEventType.BLOCK_PASTED, {
        cutBlockId: currentCutBlockId ?? undefined,
        anchorId: `${sectionPath}.blocks.${index}`,
      });
      addBlockAt(index, {
        ...val,
        _recentlyPasted: true,
      });
    }
  };

  const defaultExpanded = useMemo<Array<string>>(() => {
    return blocks.filter((s) => !s._collapse).map((s) => s.id);
  }, [blocks]);

  const handleDuplicateBlock = (block: BaseBlock & { id?: string }, index: number) => () => {
    const blockCopy = deepCopy(block);
    if (blockCopy) {
      delete (blockCopy as { id?: string }).id;
      setSettingsVisible(false);
      insert(index + 1, { ...blockCopy, _recentlyPasted: true });
      setTimeout(() => {
        // Re-show settings after move is done
        setSettingsVisible(true);
      }, 100);
    }
  };

  const handleCopyBlock = (block: BaseBlock & { id?: string }) => () => {
    const { id } = block;
    const blockCopy = deepCopy(block);
    if (id && blockCopy) {
      delete blockCopy.id;
      copy(id, blockCopy);
    }
  };

  const handleAddToClipboard = (block: BaseBlock & { id?: string }) => () => {
    const blockCopy = deepCopy(block);
    if (!blockCopy) return;
    delete blockCopy.id;
    delete blockCopy._recentlyPasted;
    moduleClipboard.add({ block: blockCopy, siteId: page.site_id, pageId: page.id, pageTitle: page.title });
    enqueueSnackbar(t('moduleClipboard.added', { name: t(`BlockType.${block.type}`) }), { variant: 'success' });
  };

  const handlePasteFromClipboard = (block: BaseBlock) => {
    const blockCopy = deepCopy(block);
    if (!blockCopy) return;
    const index = getAddIndex();
    addBlockAt(index, { ...blockCopy, _recentlyPasted: true });
    if (useEventManager) {
      setTimeout(() => {
        useEventManager.fireEvent(EditorEventType.BLOCK_OR_SECTION_ADDED, {
          anchorId: `${sectionPath}.blocks.${index}`,
        });
      });
    }
  };

  const handleCutBlock = (block: BaseBlock & { id?: string }) => () => {
    const { id } = block;
    const blockCopy = deepCopy(block);
    if (id && blockCopy) {
      delete blockCopy.id;
      cut(id, blockCopy, () => {
        setSettingsVisible(false);
        remove(blocks.findIndex((b) => b.id === id));
        setTimeout(() => {
          // Re-show settings after move is done
          setSettingsVisible(true);
        }, 100);
      });
    }
  };

  // Lets a block add one right below itself, as a text block does when it is split
  const sectionBlocks = useMemo(
    () => ({ insertBlockAfter: (index: number, block: BaseBlock) => addBlockAt(index + 1, block) }),
    // addBlockAt only depends on the number of blocks
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks.length],
  );

  const handleMove = (fromIndex: number, toIndex: number) => {
    // Its critical that settings are hidden during move to prevent rendering issues!!!!!!!!!!!!!!!!!!!!!!!!!!
    setSettingsVisible(false);
    move(fromIndex, toIndex);
    setTimeout(() => {
      // Re-show settings after move is done
      setSettingsVisible(true);
    }, 100);
  };

  const handleRemove = (index: number) => {
    setSettingsVisible(false);
    remove(index);
    setTimeout(() => {
      // Re-show settings after move is done
      setSettingsVisible(true);
    }, 100);
  };

  return (
    <SectionBlocksContext.Provider value={sectionBlocks}>
      <SectionsController defaultExpanded={defaultExpanded} nestedController eventManager={useEventManager}>
        <CommonSectionContent />
        {splitMode && (
          <Alert severity='info' variant='outlined' sx={{ width: '100%' }}>
            {t('splitSectionInfo')}
          </Alert>
        )}
        {!blocks.length && (
          <Alert variant='outlined' severity='warning' sx={{ width: '100%', justifyContent: 'center' }}>
            {t('noContent')}
          </Alert>
        )}
        {blocks.map((block, i) => (
          <Fragment key={block.id}>
            {splitMode && onSplitSection && i > 0 && settingsVisible && (
              <SplitSectionGap
                onSplit={() => {
                  setSplitMode(false);
                  onSplitSection(i - 1);
                }}
              />
            )}
            <CurrentBlockProvider block={block} blockIndex={i} sectionIndex={sectionIndex}>
              <Section
                isHidden={block.visible === 'hidden'}
                type={Draggable.BLOCK}
                color={Styles.Colors.BLOCK}
                sectionId={`${`${sectionPath}.blocks` as BlocksPath}.${i}`}
                title={t(`BlockType.${block.type}`)}
                subTitle={block.title || ''}
                headerActions={
                  <>
                    {settingsVisible && (
                      <BlockSettings
                        onDuplicateBlock={handleDuplicateBlock(block, i)}
                        onRemoveBlock={() => handleRemove(i)}
                        onCopyBlock={handleCopyBlock(block)}
                        onCutBlock={handleCutBlock(block)}
                        onAddToClipboard={canUseModuleClipboard ? handleAddToClipboard(block) : undefined}
                      />
                    )}
                    {!editorConfiguration.isBlockAvailable(block.type) && <MaterialSymbol name='error' color='error' />}
                  </>
                }
                index={i}
                value={block}
                onAdd={(item, index) => item && insert(index, item)}
                onRemove={handleRemove}
                onReorder={handleMove}
                draggable={block.type !== BlockType.ARTICLE_TITLE}
                isCut={currentCutBlockId === block.id}
                isPasted={block._recentlyPasted}
                useEventManager={useEventManager}>
                <EditorBlock />
              </Section>
            </CurrentBlockProvider>
          </Fragment>
        ))}
        <AddBlockButton
          onAddBlock={appendAndOpen}
          onPaste={handlePasteBlock}
          onPasteFromClipboard={canUseModuleClipboard ? handlePasteFromClipboard : undefined}
        />
        {onSplitSection && blocks.length > 1 && (
          <Button
            size='small'
            color='secondary'
            variant={splitMode ? 'contained' : 'text'}
            onClick={() => setSplitMode((on) => !on)}
            startIcon={<MaterialSymbol name='content_cut' sx={{ fontSize: 16 }} />}
            sx={{ alignSelf: 'flex-end', fontSize: '0.7rem' }}>
            {t(splitMode ? 'splitSectionDone' : 'splitSectionStart')}
          </Button>
        )}
      </SectionsController>
    </SectionBlocksContext.Provider>
  );
};

export default PageEditorSectionContent;
